import { useTiming } from '@/hooks/useTiming';
import { logger } from '@/services/logger';
import { hashCode } from '@/utils/utils';
import { StoreType } from 'polotno/model/store';
import { useCallback, useRef } from 'react';
import { Page } from '../../AdvancedEditor';
import { PolotnoStoreActivePageKeys, setPageProperty } from '../../hooks';

const createPageThumbnail = async (page: Page) => {
    // if page is empty and has no background, we don't need to create thumbnail
    if (!page.children.length && !page.background) {
        return '';
    }

    try {
        return await page.store.toDataURL({ pageId: page.id, pixelRatio: 0.3, quickMode: true });
    } catch (error) {
        console.error('Failed to create page thumbnail', error);
        logger.info('Failed to update scene thumbnail', error);
        return '';
    }
};

const getPagesToUpdate = (store: StoreType, pagesHashes: Map<string, number>) => {
    return store.pages.filter(page => {
        const hash = pagesHashes.get(page.id);
        const newHash = hashCode(
            JSON.stringify({ children: page.children, background: page.background, width: page.width, height: page.height })
        );

        if (hash === newHash) {
            return false;
        }

        pagesHashes.set(page.id, newHash);
        return true;
    });
};

export const useCreatePagesThumbnails = () => {
    const { withTiming } = useTiming();
    const pagesHashes = useRef(new Map<string, number>());

    const createThumbnails = useCallback(
        async (currentStore: StoreType) => {
            if (!currentStore.pages.length) {
                pagesHashes.current.clear();
                return;
            }

            const pagesToUpdate = getPagesToUpdate(currentStore, pagesHashes.current);
            if (!pagesToUpdate.length) {
                return;
            }

            await currentStore.waitLoading();

            await withTiming('createThumbnails.all', () =>
                Promise.all(
                    pagesToUpdate.map(async page => {
                        const thumbnail = await createPageThumbnail(page);
                        setPageProperty(page, PolotnoStoreActivePageKeys.thumbnail)(thumbnail);
                    })
                )
            );
        },
        [withTiming]
    );

    return { createThumbnails };
};
